import { useMemo } from "react";

import {
  type IMarket,
  type IOutcomeConsensus,
  MarketStatus,
  type MarketStatusValue,
} from "~/types/event";

import { getHiddenMarketsNote } from "./getHiddenMarketsNote";
import type {
  IMarketsDataGridMarketRow,
  IMarketsDataGridNoteRow,
  IMarketsDataGridRow,
} from "./types";

interface IUseMarketsDataGridRowsParams {
  markets: IMarket[];
  outcomes: readonly IOutcomeConsensus[];
  isUngrouped: boolean;
}

interface ICreateMarketRowOptions {
  isInGroup?: boolean;
}

const GROUP_ROW_ID_PREFIX = "market-group:";
const NOTE_ROW_ID_PREFIX = "market-group-note:";

const GROUPABLE_MARKET_STATUSES = new Set<MarketStatusValue>([
  MarketStatus.ACTIVE,
  MarketStatus.DISPUTED,
  MarketStatus.PROPOSED,
]);

const createMarketRow = (
  market: IMarket,
  { isInGroup = false }: ICreateMarketRowOptions = {}
): IMarketsDataGridMarketRow => ({
  ...market,
  rowType: "market",
  isInGroup,
});

const hasGroupableMarket = (markets: IMarket[]) =>
  markets.some((market) => GROUPABLE_MARKET_STATUSES.has(market.unifiedStatus));

const shouldGroupOutcome = (
  markets: IMarket[],
  outcome: IOutcomeConsensus | undefined
) =>
  (outcome ? outcome.marketCount : markets.length) > 1 &&
  (!markets.length || hasGroupableMarket(markets));

const createGroupRow = (
  groupKey: string,
  markets: IMarket[],
  outcome: IOutcomeConsensus | undefined
): IMarketsDataGridRow => ({
  id: `${GROUP_ROW_ID_PREFIX}${groupKey}`,
  rowType: "group",
  groupedMarkets: markets,
  outcome: outcome ?? null,
  outcomeTitle: markets.length ? null : (outcome?.title ?? null),
});

const createNoteRows = (
  groupKey: string,
  outcome: IOutcomeConsensus | undefined
): IMarketsDataGridNoteRow[] => {
  const text = outcome ? getHiddenMarketsNote(outcome) : null;

  return text
    ? [{ id: `${NOTE_ROW_ID_PREFIX}${groupKey}`, rowType: "note", text }]
    : [];
};

export const useMarketsDataGridRows = ({
  markets,
  outcomes,
  isUngrouped,
}: IUseMarketsDataGridRowsParams) => {
  return useMemo(() => {
    if (isUngrouped) {
      return markets.map((market) => createMarketRow(market));
    }

    const outcomeByKey = new Map(
      outcomes.map((outcome) => [outcome.marketGroupKey, outcome])
    );
    const marketsByGroupKey = new Map<string, IMarket[]>();

    for (const market of markets) {
      if (!market.marketGroupKey) {
        continue;
      }

      const groupMarkets = marketsByGroupKey.get(market.marketGroupKey) ?? [];
      groupMarkets.push(market);
      marketsByGroupKey.set(market.marketGroupKey, groupMarkets);
    }

    const rows: IMarketsDataGridRow[] = [];
    const processedGroupKeys = new Set<string>();

    for (const market of markets) {
      const groupKey = market.marketGroupKey;
      const groupMarkets = groupKey
        ? marketsByGroupKey.get(groupKey)
        : undefined;

      if (
        !groupKey ||
        !groupMarkets ||
        !shouldGroupOutcome(groupMarkets, outcomeByKey.get(groupKey))
      ) {
        rows.push(createMarketRow(market));
        continue;
      }

      if (processedGroupKeys.has(groupKey)) {
        continue;
      }

      rows.push(
        ...groupMarkets.map((groupMarket) =>
          createMarketRow(groupMarket, { isInGroup: true })
        )
      );

      const outcome = outcomeByKey.get(groupKey);

      rows.push(
        createGroupRow(groupKey, groupMarkets, outcome),
        ...createNoteRows(groupKey, outcome)
      );

      processedGroupKeys.add(groupKey);
    }

    for (const outcome of outcomes) {
      const groupKey = outcome.marketGroupKey;
      const isHiddenByFilters =
        outcome.matchingMarketCount === 0 && !marketsByGroupKey.has(groupKey);

      if (isHiddenByFilters && shouldGroupOutcome([], outcome)) {
        rows.push(
          createGroupRow(groupKey, [], outcome),
          ...createNoteRows(groupKey, outcome)
        );
      }
    }

    return rows;
  }, [isUngrouped, markets, outcomes]);
};
